import { useState, useEffect, useCallback, useRef } from 'react';
import { ulid } from 'ulidx';

const SESSION_STORAGE_KEY = 'queueSessionId';

interface NotOpenResponse {
  status: 'not_open';
  opensAt: string;
  saleStartsAt: string;
  pollAfterSeconds: number;
}

interface QueuedResponse {
  sessionId: string;
  status: 'queued';
  saleStartsAt: string;
  peopleAhead?: number;
  estimatedWaitSeconds?: number;
  pollAfterSeconds: number;
}

interface ActiveResponse {
  sessionId: string;
  status: 'active';
  activatedAt: string;
  expiresAt: string;
  pollAfterSeconds: number;
}

interface ConvertedResponse {
  sessionId: string;
  status: 'converted';
  pollAfterSeconds: number;
}

interface ExpiredResponse {
  sessionId: string;
  status: 'expired';
  pollAfterSeconds: number;
}

export type QueueResponse = NotOpenResponse | QueuedResponse | ActiveResponse | ConvertedResponse | ExpiredResponse;

function getOrCreateSessionId(): string {
  const stored = localStorage.getItem(SESSION_STORAGE_KEY);
  if (stored) return stored;
  const id = ulid();
  localStorage.setItem(SESSION_STORAGE_KEY, id);
  return id;
}

function resetSessionId(): string {
  localStorage.removeItem(SESSION_STORAGE_KEY);
  return getOrCreateSessionId();
}

async function generateFingerprint(): Promise<string> {
  const raw = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    screen.colorDepth,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  ].join('|');

  const encoder = new TextEncoder();
  const data = encoder.encode(raw);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return 'sha256-' + hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

const MAX_BACKOFF_MS = 60_000;

function useQueue(queueId: string | undefined, queueEndpoint: string | undefined) {
  const [queueStatus, setQueueStatus] = useState<QueueResponse | null>(null);
  const [isError, setIsError] = useState(false);
  const [saleStartsAt, setSaleStartsAt] = useState<string | null>(null);
  const sessionIdRef = useRef<string>(getOrCreateSessionId());
  const timeoutRef = useRef<number>();
  const fingerprintRef = useRef<string>('');
  const errorCountRef = useRef(0);

  const poll = useCallback(async () => {
    if (!queueId || !queueEndpoint) return;

    try {
      if (!fingerprintRef.current) {
        fingerprintRef.current = await generateFingerprint();
      }

      const response = await fetch(`${queueEndpoint}/q/${queueId}/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          fingerprint: fingerprintRef.current,
        }),
      });

      if (!response.ok) {
        throw new Error(`Queue API error: ${response.status}`);
      }

      const data: QueueResponse = await response.json();

      // Success — reset error state
      errorCountRef.current = 0;
      setIsError(false);

      if (data.status === 'not_open' || data.status === 'queued') {
        setSaleStartsAt(data.saleStartsAt);
      }

      if (data.status === 'expired') {
        // Clear session and restart with new ID
        sessionIdRef.current = resetSessionId();
        // Poll again immediately with new session
        timeoutRef.current = window.setTimeout(poll, 1000);
        return;
      }

      setQueueStatus(data);

      // Schedule next poll using server-provided interval
      timeoutRef.current = window.setTimeout(poll, data.pollAfterSeconds * 1000);
    } catch (error) {
      console.error('Failed to fetch queue status:', error);
      errorCountRef.current += 1;
      setIsError(true);
      // Exponential backoff: 5s, 10s, 20s, 40s, capped at 60s
      const backoff = Math.min(5000 * Math.pow(2, errorCountRef.current - 1), MAX_BACKOFF_MS);
      timeoutRef.current = window.setTimeout(poll, backoff);
    }
  }, [queueId, queueEndpoint]);

  useEffect(() => {
    if (!queueId || !queueEndpoint) return;

    poll();

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [queueId, queueEndpoint, poll]);

  return {
    queueStatus,
    sessionId: sessionIdRef.current,
    isError,
    saleStartsAt,
  };
}

export default useQueue;
